import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  getTokens,
  isUsableRefreshToken,
  selectProductTokenByName,
} from '@rsos/sinatra';
import { fetchOrgTokenSuccess } from '@rsos/sinatra/src/actions/fetchOrgToken';
import { refreshUserTokenSuccess } from '@rsos/sinatra/src/actions/refreshUserToken';
import { setTokens } from '@rsos/sinatra/src/utils/tokens';
import { getAPIHost, getProductName } from '@rsos/utils/metaTags';
import onLogout from '@rsos/utils/onLogout';
import {
  captureExceptionWithScope,
  clearGlobalScope,
} from '@rsos/utils/sentry';
import { notifySessionExpired } from './sessionExpiredNotice';

const apiHost = getAPIHost();

export const TOKEN_TYPE_ACCESS_TOKEN = 'ACCESS_TOKEN';
export const TOKEN_TYPE_PRODUCT_TOKEN = 'PRODUCT_TOKEN';
export const CURRENT_ORGANIZATION_NAME = 'CURRENT_ORGANIZATION_NAME';

// Authentication localStorage keys that should be cleared during logout
const AUTH_STORAGE_KEYS = ['@scorpius/tokens', 'productToken'];

/**
 * Latches session teardown to once per page load.
 *
 * A single expiry 401s every request in flight at that moment, and each one
 * reaches the teardown independently. `retry.fail` stops one request's retries
 * but says nothing about its siblings, so without this the user gets one toast
 * and one logout per in-flight request — and holding the notice on screen
 * widens the window for them to pile up.
 */
let sessionTeardownStarted = false;

/**
 * Clears the once-per-page teardown latch.
 *
 * Production never needs this: the teardown ends in a navigation, which
 * discards the module. Tests drive the teardown repeatedly in one module
 * instance and would otherwise see only the first case run.
 */
export const resetSessionTeardownForTesting = () => {
  sessionTeardownStarted = false;
};

/**
 * Clear only authentication-related items from localStorage
 */
const clearAuthFromStorage = () => {
  AUTH_STORAGE_KEYS.forEach(key => {
    localStorage.removeItem(key);
  });
};

const REFRESH_ACCESS_TOKEN_ENDPOINT = 'v1/scorpius/user/api-token-auth/refresh';
const getProductTokenEndpoint = state => {
  const orgID = state?.sinatra?.user?.currentOrg?.id;
  const productName = getProductName();
  return `v1/scorpius/organizations/${orgID}/token/?product=${productName}`;
};

/**
 * Returns product token and expiration for the current product.
 * @param {Object} sinatra - sinatra state
 */
export const getProductToken = sinatra => {
  const productName = getProductName();
  const { token: productToken, expiration: productTokenExpiration } =
    selectProductTokenByName(sinatra, productName);
  return {
    productToken,
    productTokenExpiration,
  };
};

/**
 * Redux Toolkit baseQueryFn accepted args
 */
export const baseQueryArgs = {
  headers: {
    Authorization: `Bearer ${TOKEN_TYPE_ACCESS_TOKEN}`,
  },
  validateStatus: response => {
    return response.status >= 200 && response.status < 300;
  },
};
/**
 * Base extra options for RSOS API with RTK Query
 */
export const baseExtraOptions = {
  tokenType: TOKEN_TYPE_ACCESS_TOKEN,
};

/**
 * Allow for configuration of which token to use based on the actual
 * Authorization header value. This allows the retry/refresh logic to use the
 * updated store's token values compared to having to hardcode the new token
 * within the retry request.
 * @param {Object} prepareHeaders.headers
 * @param {Object} prepareHeaders.headers.Authorization - possible values are
 * `TOKEN_TYPE_ACCESS` and `TOKEN_TYPE_PRODUCT
 * @param {Object} api - destructured to getState, extra, endpoint, type, forced
 */
const baseQuery = fetchBaseQuery({
  baseUrl: apiHost,
  prepareHeaders: (headers, { getState }) => {
    const sinatra = getState().sinatra;

    if (!headers.get('Authorization')) {
      throw new Error('Must include Authorization header with your request');
    }

    // Use the product/org token
    if (headers.get('Authorization') === `Bearer ${TOKEN_TYPE_PRODUCT_TOKEN}`) {
      const bearerToken = getProductToken(sinatra).productToken;
      headers.set('Authorization', `Bearer ${bearerToken}`);
      return headers;
    }

    const organizationName = getState().sinatra?.user?.currentOrg?.name;
    if (
      headers.get('X-RapidSOS-Org') === CURRENT_ORGANIZATION_NAME &&
      organizationName
    ) {
      headers.set('X-RapidSOS-Org', organizationName);
    }

    // Fall back to using the access token
    const token = sinatra?.auth?.tokens?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/** The refresh succeeded and the original request can be retried. */
export const REFRESH_OUTCOME_REFRESHED = 'refreshed';
/** The credential was rejected (or is unusable); the session is over. */
export const REFRESH_OUTCOME_SESSION_ENDED = 'session-ended';
/** The refresh could not be completed, but the credential is still good. */
export const REFRESH_OUTCOME_RETRYABLE = 'retryable';

/**
 * Decides whether a failed refresh means the session is over or just that we
 * could not reach a verdict this time.
 *
 * Only a response that actually came back from the server and rejected the
 * request ends a session. Transport errors, timeouts, unparseable responses
 * (typically an HTML error page from a proxy) and 5xx are infrastructure
 * problems: the credential in hand may well still be valid, and destroying the
 * session over them turns a momentary blip into a forced re-login.
 *
 * 4xx other than 401/403 is treated as terminal rather than transient. Such a
 * request will fail identically forever, so retrying would leave the user in a
 * silently frozen session — the worse of the two failure modes.
 * @param {number|string|undefined} status
 * @returns {typeof REFRESH_OUTCOME_SESSION_ENDED | typeof REFRESH_OUTCOME_RETRYABLE}
 */
export const classifyRefreshFailure = status => {
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return REFRESH_OUTCOME_SESSION_ENDED;
  }
  return REFRESH_OUTCOME_RETRYABLE;
};

/**
 * Reports a token-refresh failure to Sentry with the structured status of the
 * refresh call itself (FETCH_ERROR / 401 / 403 / 5xx / …) plus the endpoint
 * whose 401 triggered the refresh. Replaces bare console.error calls whose
 * object argument was mangled to "[object Object]" in Sentry — hiding the very
 * status needed to tell a network blip apart from an expired/revoked token.
 * Fingerprint keeps each (token kind × status) in its own issue.
 *
 * `outcome` goes in tags rather than in the message or the fingerprint, so the
 * existing issues stay the same issues across this change. That matters for
 * verification: the point of the fix is that specific issues (the `access 400`
 * cluster) stop receiving events, which is only evidence if they cannot
 * silently re-form under a new title.
 */
export const reportTokenRefreshFailure = (
  tokenKind,
  refreshResult,
  api,
  outcome = REFRESH_OUTCOME_SESSION_ENDED,
) => {
  const rawStatus = refreshResult?.error?.status;
  const status =
    typeof rawStatus === 'number' || typeof rawStatus === 'string'
      ? rawStatus
      : 'error';
  captureExceptionWithScope(
    new Error(`[token refresh failed] ${tokenKind} ${status}`),
    {
      tokenKind,
      status,
      outcome,
      originatingEndpoint: api?.endpoint ?? 'unknown',
      error: refreshResult?.error ?? refreshResult,
    },
    ['token-refresh-error', tokenKind, String(status)],
    { tokenKind, tokenRefreshOutcome: outcome },
  );
};

/**
 * Custom query with handling to refresh the access or product token based on
 * `extraOptions.tokenType`. It will refresh both if that isn't passed in to
 * the queryFn
 */
/**
 * In-flight refresh promises, keyed by token kind.
 *
 * A single access-token expiry 401s every request in flight at that moment,
 * and `staggeredBaseQuery` re-enters this whole function on each retry, so
 * without this one expiry fans out into many concurrent refreshes. They race:
 * each one POSTs the same refresh token, and the losers apply a token the
 * server has already rotated away from.
 */
const inFlightRefreshes = new Map();

const withSingleFlight = (tokenKind, run) => {
  const existing = inFlightRefreshes.get(tokenKind);
  if (existing) {
    return existing;
  }
  const pending = run().finally(() => {
    inFlightRefreshes.delete(tokenKind);
  });
  inFlightRefreshes.set(tokenKind, pending);
  return pending;
};

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    const refreshAccessToken = () =>
      withSingleFlight(TOKEN_TYPE_ACCESS_TOKEN, async () => {
        const refreshToken = getTokens()?.refresh_token;

        // IRP sessions and legacy `?access_token=` auto-login links have no
        // refresh token. Posting one anyway is a guaranteed 400 from the
        // server, and the old code read that 400 as "credential rejected" and
        // logged the user out. There is nothing to refresh here, so say so
        // directly instead of asking the server to tell us.
        if (!isUsableRefreshToken(refreshToken)) {
          reportTokenRefreshFailure(
            'access',
            { error: { status: 'NO_REFRESH_TOKEN' } },
            api,
            REFRESH_OUTCOME_SESSION_ENDED,
          );
          return REFRESH_OUTCOME_SESSION_ENDED;
        }

        const refreshAccessTokenResult = await baseQuery(
          {
            url: REFRESH_ACCESS_TOKEN_ENDPOINT,
            // The endpoint reads the body, not this header — `prepareHeaders`
            // replaces the value below with the (expired) access token before
            // the request leaves. It still has to be set to something:
            // `prepareHeaders` throws outright on a missing Authorization
            // header. Reconciling that belongs with the wider token-source
            // cleanup, not with this fix.
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
            method: 'POST',
            body: { refresh_token: refreshToken },
          },
          api,
          extraOptions,
        );

        if (refreshAccessTokenResult.data) {
          const accessToken = refreshAccessTokenResult.data.token;
          api.dispatch(refreshUserTokenSuccess(accessToken));

          setTokens({
            token: accessToken,
            refresh_token: refreshToken,
          });
          return REFRESH_OUTCOME_REFRESHED;
        }

        const outcome = classifyRefreshFailure(
          refreshAccessTokenResult?.error?.status,
        );
        reportTokenRefreshFailure(
          'access',
          refreshAccessTokenResult,
          api,
          outcome,
        );
        return outcome;
      });

    const refreshProductToken = () =>
      withSingleFlight(TOKEN_TYPE_PRODUCT_TOKEN, async () => {
        const userToken = getTokens()?.token;
        const refreshProductTokenResult = await baseQuery(
          {
            url: getProductTokenEndpoint(api.getState()),
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          },
          api,
          extraOptions,
        );
        if (refreshProductTokenResult.data) {
          const productToken = refreshProductTokenResult.data.token;
          api.dispatch(fetchOrgTokenSuccess(productToken));
          return REFRESH_OUTCOME_REFRESHED;
        }

        const outcome = classifyRefreshFailure(
          refreshProductTokenResult?.error?.status,
        );
        reportTokenRefreshFailure(
          'product',
          refreshProductTokenResult,
          api,
          outcome,
        );
        return outcome;
      });

    /**
     * Combines the outcomes of refreshing both tokens. The session ends only if
     * one of them was actually rejected; if either was merely unreachable we
     * keep the session and let the caller retry.
     */
    const combineOutcomes = (accessOutcome, productOutcome) => {
      if (
        accessOutcome === REFRESH_OUTCOME_SESSION_ENDED ||
        productOutcome === REFRESH_OUTCOME_SESSION_ENDED
      ) {
        return REFRESH_OUTCOME_SESSION_ENDED;
      }
      if (
        accessOutcome === REFRESH_OUTCOME_RETRYABLE ||
        productOutcome === REFRESH_OUTCOME_RETRYABLE
      ) {
        return REFRESH_OUTCOME_RETRYABLE;
      }
      return REFRESH_OUTCOME_REFRESHED;
    };

    let outcome;

    try {
      if (extraOptions?.tokenType === TOKEN_TYPE_ACCESS_TOKEN) {
        outcome = await refreshAccessToken();
      } else if (extraOptions?.tokenType === TOKEN_TYPE_PRODUCT_TOKEN) {
        outcome = await refreshProductToken();
      } else {
        outcome = combineOutcomes(
          await refreshAccessToken(),
          await refreshProductToken(),
        );
      }
    } catch (error) {
      captureExceptionWithScope(
        error instanceof Error
          ? error
          : new Error(`[token refresh failed] exception ${String(error)}`),
        {
          phase: 'token-refresh-exception',
          originatingEndpoint: api?.endpoint ?? 'unknown',
        },
        ['token-refresh-error', 'exception'],
      );
      // An exception here is a bug in this code, not a verdict from the server
      // about the credential. Keep the session and let the request retry.
      outcome = REFRESH_OUTCOME_RETRYABLE;
    }

    if (outcome === REFRESH_OUTCOME_RETRYABLE) {
      // The credential was never rejected — we just could not reach a verdict
      // (network dropped, gateway 5xx, unparseable response). Destroying the
      // session here is what turns a momentary blip into a forced re-login.
      // Surface the original 401 so `staggeredBaseQuery` retries it.
      return result;
    }

    // Handle refresh failure - log user out
    if (outcome === REFRESH_OUTCOME_SESSION_ENDED) {
      if (sessionTeardownStarted) {
        // A sibling request already owns the teardown. Bail out the same way it
        // will, without a second toast, logout or navigation.
        return retry.fail(result.error, result.meta);
      }
      sessionTeardownStarted = true;

      // Order matters here: notify, then log out, then clear. Wiping storage
      // first means every later step runs against a session that is already
      // unrecoverable, so anything that goes wrong after it strands the user in
      // a tab that still looks signed in and can no longer make a request.
      // Clearing last keeps the bad credential alive only for as long as it
      // takes to get the user out.
      //
      // The notice is awaited rather than fired and forgotten: the navigation
      // below otherwise wins the race against the first paint and the user
      // arrives at the login screen with no idea why.
      await notifySessionExpired();

      try {
        // The shared portal logout: resets Redux through the sinatra logout
        // action every app's reducers already react to, purges the persisted
        // caches, and navigates to the login screen (/ns for NS SSO sessions).
        // `api.dispatch` belongs to the store that issued this request, so the
        // teardown lands in the right store without anything being registered
        // in advance.
        await onLogout(undefined, api.dispatch);
      } catch (error) {
        captureExceptionWithScope(
          error instanceof Error
            ? error
            : new Error(`[logout failed] ${String(error)}`),
          { phase: 'auth-teardown' },
          ['auth-teardown', 'logout-error'],
        );
      }

      // Clear the rejected credential last, and unconditionally: it must not
      // survive even when the steps above failed. Clearing the Sentry scope
      // with it stops the next session inheriting this user's context.
      clearAuthFromStorage();
      clearGlobalScope();

      // Stop the retry wrapper dead rather than returning the error normally.
      // `retry`'s default condition is `attempt <= maxRetries` and never looks
      // at the status, so a returned 401 gets retried — running this whole
      // teardown once per attempt: three storage wipes, three toasts, three
      // logout navigations, three Sentry events. `retry.fail` sets
      // `throwImmediately`, which the wrapper honours by returning the value
      // as-is. The session is over; there is nothing to retry.
      return retry.fail(result.error, result.meta);
    }

    // This will use the refreshed token for the retry
    result = await baseQuery(args, api, extraOptions);
  }

  return result;
};

/**
 * RTK automatic retry utility with exponential backoff. With no `retryCondition`
 * configured, RTK Query retries on *every* failed query — network FETCH_ERRORs
 * and TIMEOUT_ERRORs included, not just 5xx responses — up to `maxRetries`
 * before the rejection finally surfaces to the caller.
 */
export const staggeredBaseQuery = retry(baseQueryWithReauth, {
  maxRetries: 2,
});

export const rsosApi = createApi({
  reducerPath: 'api',
  baseQuery: staggeredBaseQuery,
  endpoints: () => ({}),
});
